import React, { PureComponent } from "react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ReferenceDot,
} from "recharts";

import {
  Metric,
  selectFormat,
  Error,
  CustomizedYAxisTick,
  CustomizedXAxisTickTimeDay,
  StandardTooltip,
  isDateFromDataInISOFormat,
  formatDateFromData,
  MAIN_COLORS,
} from "components/Utils/Utils";

import { XRayTooltip } from "components/Utils/XRayTooltip/XRayTooltip";

import Heading from "components/Elements/Heading";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsTickIndicator,
  InsightsLineLabel,
} from "components/Utils/InsightsUtils.js";

import { getXrayTooltipPosition, closeAllTooltips } from "helpers/DOM";

class PocketWatchVideoPerformance extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };
    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllTooltips("xray-tooltip-close-icon");

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  renderLegends = (props) => {
    return props.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span
          className="legend__icon"
          style={{ background: entry.color }}
        ></span>
        <span className="legend__text" style={{ color: entry.color }}>
          {entry.label}
        </span>
      </span>
    ));
  };

  getExportData = () => ({
    data: this.props.data.data.graph,
    opts: {
      type: "chart",
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.data.graph),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let widget_data = data.data.graph;

    const insightsData = widgetsInsights.length > 0;

    let preparedData = formatDateFromData(widget_data);

    return (
      <div>
        <div className="row">
          <div className="col-6 col-lg-3 mb-5 mb-lg-0">
            <Heading size={6} className={"mb-1"}>
              Total Views
            </Heading>
            <Metric {...data.data.views_total} size={"lg"} />
          </div>

          <div className="col-6 col-lg-3 mb-5 mb-lg-0">
            <Heading size={6} className={"mb-1"}>
              Avg Daily Views
            </Heading>
            <Metric {...data.data.views_daily_avg} size={"lg"} />
          </div>

          <div className="col-6 col-lg-3 mb-5 mb-lg-0">
            <Heading size={6} className={"mb-1"}>
              Total Video Ads
            </Heading>
            <Metric {...data.data.ads_total} size={"lg"} />
          </div>

          <div className="col-6 col-lg-3 mb-5 mb-lg-0">
            <Heading size={6} className={"mb-1"}>
              Avg Daily Video Ads
            </Heading>
            <Metric {...data.data.ads_daily_avg} size={"lg"} />
          </div>
        </div>

        {/* {this.renderLegends(fieldProps)} */}
        <div className="d-flex mt-5" ref={this.divPosition}>
          <ResponsiveContainer height={340} width="100%">
            <LineChart
              width={600}
              height={400}
              data={preparedData}
              margin={{ top: 12, right: 30, left: 0, bottom: 20 }}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              isAnimationActive={false}
              onClick={(d) => {
                // const extra = [
                //   {
                //     key: d.activePayload[0].dataKey,
                //     value: d.activePayload[0].value,
                //     color: d.activePayload[0].stroke,
                //   },
                //   {
                //     key: d.activePayload[1].dataKey,
                //     value: d.activePayload[1].value,
                //     color: d.activePayload[1].stroke,
                //   },
                // ];

                // d.extra = extra;

                this.setState({
                  tooltip: { ...d, show: true },
                });
              }}
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              <Line
                type="monotone"
                dataKey="Video Views"
                stroke={MAIN_COLORS[0]}
                strokeWidth={2}
                isAnimationActive={false}
              />

              <Line
                type="monotone"
                dataKey="Video Ads"
                stroke={MAIN_COLORS[1]}
                strokeWidth={2}
                isAnimationActive={false}
              >
                {this.state.widgetsInsights.length > 0 && (
                  <LabelList
                    dataKey="date"
                    content={
                      <InsightsLineLabel
                        height={290}
                        data={preparedData}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Line>

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                scale="auto"
                tick={<CustomizedXAxisTickTimeDay />}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
              />

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={this.node}
                      />
                    }
                  />
                ))}

              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);
                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header">
                                  Date:
                                </span>
                                <span className="standard-tooltip__value-header">
                                  {tooltip_data.date}
                                </span>
                              </header>

                              <main className="standard-tooltip__main">
                                {payload
                                  .sort((a, b) => b.value - a.value)
                                  .map((key) => {
                                    return (
                                      <div
                                        className="standard-tooltip__item"
                                        key={key.dataKey}
                                      >
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: key.color,
                                          }}
                                        />
                                        <span
                                          className="standard-tooltip__key"
                                          style={{
                                            color: key.color,
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {key.dataKey}
                                        </span>
                                        <span className="standard-tooltip__value">
                                          {selectFormat("number", key.value)}
                                        </span>
                                      </div>
                                    );
                                  })}

                                {this.state.widgetsInsights && (
                                  <InsightsWithinTooltip
                                    data={this.state.widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
            </LineChart>
          </ResponsiveContainer>

          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: this.state.tooltip.activeCoordinate.y + 70 + "px",
                left:
                  this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                  this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                insights={widgetsInsights}
                close={this.closeTooltip.bind(this)}
              />
            </div>
          ) : null}
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={this.state.widgetsInsights} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default PocketWatchVideoPerformance;
